import { IMAGE_MAX_BYTES } from '@sitzmark/core';

/**
 * Checking a photo before it travels (SCOPE §2.2-7, decision 141).
 *
 * The server checks again and is the authority; this only saves a buyer a
 * 15 MB round trip to be told a PDF is not a photo.
 */

export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';

const ACCEPTED_TYPES = new Set(PHOTO_ACCEPT.split(','));

export function photoRejection(file: { name: string; type: string; size: number }): string | null {
  const kind = photoKindRejection(file);
  if (kind !== null) return kind;
  if (file.size > IMAGE_MAX_BYTES) {
    return `${file.name} is over 15 MB. Save a smaller copy and try again.`;
  }
  return null;
}

/**
 * Whether it is a photo at all — checked before it is shrunk, where the size
 * limit is checked after (photo-shrink.ts): a 20 MB phone photo shrinks to
 * one, and refusing it for a size it will not be sent at helps nobody.
 */
export function photoKindRejection(file: {
  name: string;
  type: string;
  size: number;
}): string | null {
  if (file.size === 0) return `${file.name} is empty.`;
  if (!ACCEPTED_TYPES.has(file.type)) {
    return `${file.name} is not a photo we can use. Use a JPEG, PNG or WebP.`;
  }
  return null;
}

/** The colours a photo can belong to: this style's own, in the order they first appear. */
export function photoColorChoices(variants: readonly { colorName: string | null }[]): string[] {
  const seen = new Set<string>();
  const choices: string[] = [];
  for (const variant of variants) {
    const color = variant.colorName?.trim() ?? '';
    if (color === '' || seen.has(color.toLowerCase())) continue;
    seen.add(color.toLowerCase());
    choices.push(color);
  }
  return choices;
}
